import { useState } from 'react';
import { Check, ChevronDown, Filter, ListFilter } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { cn } from '@/shared/lib/cn';
import type { EntityFilterSpec } from './constants';

type Props = {
  primary: EntityFilterSpec | null;
  primaryValue: string | null;
  onPrimaryChange: (value: string | null) => void;
  secondary: EntityFilterSpec | null;
  secondaryValue: string | null | undefined;
  onSecondaryChange: (value: string | null) => void;
};

export function ConfiguracionFilterPopover({
  primary,
  primaryValue,
  onPrimaryChange,
  secondary,
  secondaryValue,
  onSecondaryChange,
}: Props) {
  const [open, setOpen] = useState(false);

  const primaryActive = primary ? primaryValue !== primary.defaultValue : false;
  const secondaryActive = secondary
    ? (secondaryValue ?? null) !== secondary.defaultValue
    : false;
  const active = primaryActive || secondaryActive;

  const handleReset = () => {
    if (primary) onPrimaryChange(primary.defaultValue);
    if (secondary) onSecondaryChange(secondary.defaultValue);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('h-12 gap-2', active && 'border-foreground/60')}
        >
          <Filter className="h-4 w-4" aria-hidden />
          Filtrar
          {active && (
            <span className="ml-1 inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          )}
          <ChevronDown className="h-4 w-4 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3 p-3">
        {primary && (
          <FilterSection
            label={primary.label}
            options={primary.options}
            value={primaryValue}
            onChange={onPrimaryChange}
          />
        )}
        {secondary && (
          <FilterSection
            label={secondary.label}
            options={secondary.options}
            value={secondaryValue ?? null}
            onChange={onSecondaryChange}
          />
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={handleReset}>
            Reiniciar
          </Button>
          <Button size="sm" onClick={() => setOpen(false)}>
            Listo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterSection({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: EntityFilterSpec['options'];
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <div className="space-y-1.5">
      <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        <ListFilter className="h-3.5 w-3.5" aria-hidden /> {label}
      </p>
      <div className="flex flex-col gap-1">
        {options.map((opt) => (
          <FilterRadio
            key={opt.value ?? '__all__'}
            label={opt.label}
            checked={value === opt.value}
            onSelect={() => onChange(opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

function FilterRadio({
  label,
  checked,
  onSelect,
}: {
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition-colors',
        checked
          ? 'border-foreground bg-foreground/5 text-foreground'
          : 'border-border text-muted-foreground hover:bg-secondary',
      )}
    >
      <span>{label}</span>
      {checked && <Check className="h-4 w-4 text-emerald-600" aria-hidden />}
    </button>
  );
}
